import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EngineeringOutlined } from '@mui/icons-material';
import { SectionHeaderWithCommand, SectionPaper } from '../../../../common/components';
import { useAppSelector } from '../../../../../app/hooks';
import { EditorMode } from '../../../types/editorTypes';
import { PMCenterEditor, PMCategoryEditor, PMInstructionsEditor, PMFindingsEditor, PMPartsUsedEditor, AssetLookupEditor, PMPerformedByEditor, PMVendorAutocomplete, PMVendorContactDisplay } from '../fields/pmDetails';

interface PMDetailsSectionProps {
  mode?: EditorMode;
  showFindings?: boolean;
  showPartsUsed?: boolean;
}

/**
 * PMDetailsSection - Unified section for Preventive Maintenance fields.
 * Works for both tickets (mode='ticket') and recurring tasks (mode='recurringTask').
 * Recurring tasks hide findings and parts used (runtime data, not template data).
 */
export const PMDetailsSection: React.FC<PMDetailsSectionProps> = ({ mode = 'ticket', showFindings = true, showPartsUsed = true }) => {
  const performedBy = useAppSelector(state =>
    mode === 'ticket'
      ? state.ticket.currentTicket?.preventiveMaintenanceRequest?.performedBy
      : state.recurringTask.currentRecurringTask?.preventiveMaintenanceRequest?.performedBy
  );
  const title = mode === 'ticket' ? 'Preventive Maintenance' : 'Preventive Maintenance Template';

  return (
    <Box>
      <SectionHeaderWithCommand
        icon={<EngineeringOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title={title}
      />
      
      <SectionPaper>
        <Stack spacing={3}>
          {/* Row 1: Center + Asset Lookup */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Asset Information
            </Typography>
            <Stack spacing={2}>
              <PMCenterEditor mode={mode} />
              <AssetLookupEditor mode={mode} />
            </Stack>
          </Box>

          {/* Row 2: PM Category + Instructions */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              PM Details
            </Typography>
            <Stack spacing={2}>
              <PMCategoryEditor mode={mode} />
              <PMInstructionsEditor mode={mode} />
            </Stack>
          </Box>

          {/* Row 3: Findings */}
          {showFindings && (
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Inspection Results
            </Typography>
            <PMFindingsEditor mode={mode} />
          </Box>
          )}

          {/* Row 4: Parts Used */}
          {showPartsUsed && (
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Parts Used
            </Typography>
            <PMPartsUsedEditor mode={mode} />
          </Box>
          )}

          {/* Row 5: Performed By */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Work Assignment
            </Typography>
            <PMPerformedByEditor mode={mode} />
          </Box>

          {/* Row 6: Vendor Info (conditional) */}
          {performedBy === 'VENDOR' && (
            <Box>
              <Typography 
                variant="body2" 
                sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
              >
                Vendor Information
              </Typography>
              <Stack spacing={2}>
                <PMVendorAutocomplete mode={mode} />
                <PMVendorContactDisplay mode={mode} />
              </Stack>
            </Box>
          )}
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default PMDetailsSection;
